import { fetchAddAddress, fetchUpdateAddress } from '@/api/account';
import { Address } from '@/api/types/public';
import AddressCard from '@/app/(payment)/payment/components/Address/components/ChooseAddress/AddressCard';
import { handleDiffCountry } from '@/app/(payment)/payment/util';
import AddressForm from '@/components/Address';
import Button from '@/components/Button';
import MobileNav from '@/components/MobileNav';
import Popup from '@/components/Popup';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { EPaymentMethod } from '@/utils/enums/order';
import { env } from '@/utils/env';
import { getAddressRules } from '@/utils/form-rule';
import { RightArrowOutlined } from '@zeelool/icon';
import { useEffect, useState } from 'react';
import WithoutAddressPreview from './components/withoutAddressPreview';
import { useTranslations } from 'next-intl';

export enum AddressActionType {
  ADD = 'ADD',
  EDIT = 'EDIT',
  DEFAULT = 'DEFAULT',
}
export interface AddressComponentMobileProps {
  shippingAddress?: Address;
  addressList?: Address[];
  SDMyAddressPageBtnClick?: (btn_name: string) => void;
  selectedAddress?: Address;
  updateSelectedAddress?: (address: Address) => void;
  paymentType?: string;
  SDAddressEditPageBtnClick?: (btn_name: string, is_save_success: boolean) => void;
  SDAddressEditPageView?: () => void;
  handleUpdateAddressList?: (id?: number) => void;
}

export default function AddressComponentMobile({
  shippingAddress,
  addressList = [],
  SDMyAddressPageBtnClick = () => {},
  selectedAddress,
  updateSelectedAddress = () => {},
  paymentType,
  SDAddressEditPageBtnClick,
  SDAddressEditPageView,
  handleUpdateAddressList,
}: AddressComponentMobileProps) {
  const t = useTranslations('payment.BillingAddress');
  const [showList, setShowList] = useState(false);
  const [actionType, setActionType] = useState('none');
  const countryList = useStore((state) => state.countryList);
  const [openAddNewAddress, setOpenAddNewAddress] = useState(false);
  const defaultAddress = {
    firstname: '',
    lastname: '',
    country_id: env.NEXT_PUBLIC_ADDRESS_COUNTRY_ID,
    country: env.NEXT_PUBLIC_ADDRESS_COUNTRY,
    street: '',
    apartment: '',
    city: '',
    province: '',
    province_id: '',
    region_code: '',
    postcode: '',
    tel_code: env.NEXT_PUBLIC_ADDRESS_TEL_CODE,
    tel_country: env.NEXT_PUBLIC_ADDRESS_TEL_COUNTRY,
    telephone: '',
    cpf: '', // 巴西税号
    default: 2,
  };

  const [addressFormData, setAddressFormData] = useState<Address>(defaultAddress);

  useEffect(() => {
    if (actionType !== AddressActionType.DEFAULT) {
      SDAddressEditPageView();
    }
  }, [actionType]);

  const addNew = () => {
    setAddressFormData(defaultAddress);
    setOpenAddNewAddress(true);
    setActionType('ADD');
  };

  // 编辑地址
  const editAddress = (address: Address) => {
    const toEditAddress = [EPaymentMethod.AFTERPAY, EPaymentMethod.KLARNA].includes(paymentType)
      ? handleDiffCountry(address, shippingAddress, countryList)
      : address;
    setActionType('EDIT');
    setAddressFormData(toEditAddress);
    setOpenAddNewAddress(true);
  };

  // 切换地址
  const chooseAddress = (item: Address) => {
    if (item.status === 2 || !item.checked) return;
    if (disabledAddressItem(item)) return;

    updateSelectedAddress(item);
    setShowList(false);
  };

  const disabledAddressItem = (address: Address) => {
    const rule1 = [EPaymentMethod.AFTERPAY, EPaymentMethod.KLARNA].includes(paymentType || '');
    const rule2 = address.country_id !== shippingAddress?.country_id;
    return rule1 && rule2;
  };

  const [loading, setLoading] = useState(false);
  const handleOnSubmit = async (address: Address) => {
    setLoading(true);

    try {
      let handleId = 0;
      if (actionType === 'ADD') {
        // 新增
        // 这里的toast隐藏掉，因为它的弹出和全局的loading重复了
        const { code, data } = await fetchAddAddress(address, false);
        if (code !== 200) {
          SDAddressEditPageBtnClick('Save', false);
          setLoading(false);
          return;
        }
        handleId = data?.id || 0;
      } else {
        // 修改
        // 这里的toast隐藏掉，因为它的弹出和全局的loading重复了
        const { code } = await fetchUpdateAddress(address.id, address, false);
        if (code !== 200) {
          SDAddressEditPageBtnClick('Save', false);
          setLoading(false);
          return;
        }
        handleId = address.id;
      }

      // updateSelectedAddress(address);
      handleUpdateAddressList(handleId);
      setOpenAddNewAddress(false);
      setActionType('none');
      SDAddressEditPageBtnClick('Save', true);
    } catch (error) {
      SDAddressEditPageBtnClick('Save', false);
      logger.error('Failed to submit address:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-4">
      <div className="flex justify-between items-center" />
      <div className="mt-3 cursor-pointer">
        {!selectedAddress?.id ? (
          <WithoutAddressPreview
            onClick={() => {
              addNew();
            }}
          />
        ) : (
          <div
            className="flex items-center justify-between w-full text-primary-black"
            onClick={() => {
              setShowList(!showList);
            }}
          >
            <div>
              <div className="">
                {selectedAddress.default === 1 && (
                  <span className="inline-block py-[2px] px-[6px] text-[10px] bg-primary-purple text-white rounded-[8px] mr-4">
                    {t('default')}
                  </span>
                )}
                <span className="text-[16px] leading-[1.2] break-words font-bb">
                  {selectedAddress.address}
                </span>
              </div>
              <p className="">
                <span className="text-[14px] mr-[4px]">
                  {selectedAddress.firstname} {selectedAddress.lastname}
                </span>
                <span className="text-[14px]">{selectedAddress.telephone}</span>
              </p>
              {/* !countryIsSame 的提示在 ClientPage.tsx 中做处理了 */}
              {!!getAddressRules(selectedAddress, countryList) && (
                <div className="invalid-tips text-[12px] text-[#BC1B1B] mt-[10px]">
                  {getAddressRules(selectedAddress, countryList)}
                </div>
              )}
            </div>

            <RightArrowOutlined className="text-28" />
          </div>
        )}
      </div>

      <Popup
        open={showList}
        onClose={() => {
          setShowList(false);
        }}
        position="right"
        height="100%"
        width="100%"
        sx={{
          '& .MuiDrawer-paper': {
            borderRadius: '0',
            width: '100%',
            backgroundColor: '#f5f6f7',
          },
          '& .MuiBox-root': {
            padding: '0',
            maxHeight: '100vh',
          },
        }}
      >
        <MobileNav
          className="sticky top-0 left-0 w-full right-0 z-20 bg-[#f5f6f7]"
          titleClassName="text-center w-full text-[18px] font-bb"
          title="My Address"
          goBack={() => {
            setShowList(false);
          }}
        />
        <div className="px-12 mb-[80px]">
          {addressList.length > 0 ? (
            addressList.map((item) => (
              <AddressCard
                key={item.id}
                address={item}
                shippingAddress={shippingAddress}
                paymentType={paymentType}
                isBilling
                countryList={countryList}
                onEditClick={editAddress}
                onChooseAddress={chooseAddress}
              />
            ))
          ) : (
            <div className="py-[12px] text-center">{t('noAddressAvailable')}</div>
          )}
        </div>
        <div className="absolute bottom-0 left-0 w-full h-[55px] bg-white flex justify-center items-center">
          <Button
            variant="contained"
            onClick={() => {
              addNew();
              SDMyAddressPageBtnClick('Add New Address');
            }}
            sx={{
              width: '85%',
              height: '50px',
            }}
          >
            {t('addNewAddress')}
          </Button>
        </div>
      </Popup>

      <Popup
        open={openAddNewAddress}
        onClose={() => {
          setOpenAddNewAddress(false);
        }}
        position="right"
        height="100%"
        width="100%"
        sx={{
          '& .MuiDrawer-paper': {
            borderRadius: '0',
            width: '100%',
            backgroundColor: '#f5f6f7',
          },
          '& .MuiBox-root': {
            padding: '0',
          },
        }}
      >
        <div className="">
          <MobileNav
            className="!absolute top-0 left-0 w-full z-20 bg-[#f5f6f7]"
            titleClassName="text-center w-full text-[18px] font-bb"
            title="Billing Address"
            goBack={() => {
              setOpenAddNewAddress(false);
            }}
          />
          <div className="pt-[50px]">
            <AddressForm
              data={addressFormData}
              close={() => {
                SDAddressEditPageBtnClick('Cancel', false);
                setOpenAddNewAddress(false);
              }}
              onSubmit={handleOnSubmit}
              type={actionType as 'ADD' | 'EDIT'}
              loading={loading}
              disabledCountry={[EPaymentMethod.AFTERPAY, EPaymentMethod.KLARNA].includes(
                paymentType,
              )}
            />
          </div>
        </div>
      </Popup>
    </div>
  );
}
